// components/StorageFullBanner.tsx
import React, { useEffect, useState, useRef } from 'react';
import { FiAlertCircle, FiX, FiDatabase } from 'react-icons/fi';
import { useStorageState } from '../contexts/StorageStateContext';

// Export a hook to get banner height for spacing
export function useBannerHeight() {
  const [bannerHeight, setBannerHeight] = useState(0);
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const { storageState } = useStorageState();

  useEffect(() => {
    if (bannerRef.current) {
      setBannerHeight(bannerRef.current.offsetHeight);
    }
  }, [storageState]);

  return { bannerHeight, bannerRef };
}

/**
 * Global Storage Full Banner Component
 * Shows when storage is full (read-only mode) or almost full
 * Should be placed at the top of the app layout
 */
export default function StorageFullBanner() {
  const { storageState } = useStorageState();
  const [dismissed, setDismissed] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Reset dismissed state when storageState changes
  useEffect(() => {
    setDismissed(false);
  }, [storageState?.readOnly, storageState?.almostFull]);

  // Don't show if dismissed or no storage state
  if (dismissed || !storageState) {
    return null;
  }

  const { readOnly, almostFull, percentage } = storageState;

  // Show read-only banner (storage full)
  if (readOnly) {
    return (
      <div 
        ref={bannerRef}
        className="fixed top-[56px] left-0 right-0 z-40 bg-red-50 border-b border-red-200 px-4 py-3 shadow-sm md:top-[56px]"
        style={{ marginLeft: '0', marginRight: '0' }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-red-900">
                האחסון מלא – החשבון במצב קריאה בלבד
              </div>
              <div className="text-sm text-red-700 mt-1">
                לא ניתן להוסיף נתונים חדשים. אנא שחררו מקום או שדרגו את התוכנית.
              </div>
            </div>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
            aria-label="סגור"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // Show warning banner (almost full - 80%+)
  if (almostFull) {
    return (
      <div 
        ref={bannerRef}
        className="fixed top-[56px] left-0 right-0 z-40 bg-yellow-50 border-b border-yellow-200 px-4 py-3 shadow-sm md:top-[56px]"
        style={{ marginLeft: '0', marginRight: '0' }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <FiDatabase className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-yellow-900">
                האחסון כמעט מלא
              </div>
              <div className="text-sm text-yellow-700 mt-1">
                {Math.round(percentage)}% מהאחסון בשימוש. אנא שחררו מקום או שדרגו את התוכנית.
              </div>
            </div>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-yellow-400 hover:text-yellow-600 transition-colors flex-shrink-0"
            aria-label="סגור"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}

